import { inject, Injectable } from "@angular/core";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentReference,
  Firestore,
  getDoc,
  getDocs,
  query,
  Query,
  updateDoc,
  where
} from "@angular/fire/firestore";
import { ExternalProduction } from "../models/external-production.model";
import { SubcontractRequest } from "../models/subcontract-request.model";
import { ObjectHelper } from "../helpers/object.helper";
import { Functions, httpsCallable, HttpsCallableResult } from "@angular/fire/functions";
import { TimeSlot } from "../models/time-slot.model";
import { ExternalProductionState } from "../enums/external-production-state.enum";
import { DataService } from "./data.service";

@Injectable({
  providedIn: 'root',
})
export class ExternalProductionService {

  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private dataService = inject(DataService);

  /**
   * Get external productions filtered by states
   * @param organizationId Current organization Id
   * @returns The query observable
   */
  list(organizationId: string): Query<ExternalProduction> {
    return query(collection(this.firestore, 'external-productions'),
      where('organizationId', '==', organizationId))
      .withConverter(ExternalProduction.converter);
  }

  /**
   * Get external productions filtered by order id
   * @param organizationId Current organization id
   * @param orderId Current order id
   * @returns The query observable
   */
  listByOrderId(organizationId: string, orderId: string): Query<ExternalProduction> {
    return query(collection(this.firestore, 'external-productions'),
      where('organizationId', '==', organizationId),
      where('orderId', '==', orderId))
      .withConverter(ExternalProduction.converter);
  }

  /**
   * Get external productions filtered by quotation id
   * @param organizationId Current organization id
   * @param quotationId Current quotation id
   * @returns The query observable
   */
  listByQuotationId(organizationId: string, quotationId: string): Query<ExternalProduction> {
    return query(collection(this.firestore, 'external-productions'),
      where('organizationId', '==', organizationId),
      where('quotationId', '==', quotationId))
      .withConverter(ExternalProduction.converter);
  }

  /**
   * Get external productions by order id
   * @param organizationId Current organization id
   * @param orderId Current order id
   * @returns The query observable
   */
  async getByOrderId(organizationId: string, orderId: string): Promise<ExternalProduction[]> {
    const q = this.listByOrderId(organizationId, orderId);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Get an external production by id
   * @param id The external production id
   * @returns The external production
   */
  async get(id: string): Promise<ExternalProduction> {
    const document = await getDoc(this.getById(id));
    return document.data();
  }

  /**
   * Returns a typed Firestore document reference for a single external production.
   * Use with onSnapshot for real-time subscriptions, or with getDoc for a one-time fetch.
   * @param id The external production id
   * @returns A typed DocumentReference
   */
  getById(id: string): DocumentReference<ExternalProduction> {
    return doc(this.firestore, 'external-productions', id)
      .withConverter(ExternalProduction.converter);
  }

  /**
   * Delete an external production by id
   * @param id The external production id
   */
  async delete(id: string): Promise<void> {
    await deleteDoc(this.getById(id));
  }

  /**
   * Update an external production
   * @param id The external production id
   * @param externalProduction The external production to save
   */
  async update(id: string, externalProduction: Partial<ExternalProduction>): Promise<void> {
    const ref = doc(this.firestore, 'external-productions', id)
      .withConverter(ExternalProduction.converter);
    await updateDoc(ref, ObjectHelper.anonymize(externalProduction));
  }

  /**
   * Send request email
   * @param externalProductionId The external production id
   * @returns The promise
   */
  public sendRequest(externalProductionId: string): Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ externalProductionId: string }, void>(
      this.functions, 'send_request_gen2')(
      { externalProductionId });
  }

  /**
   * Resend access link to the selected subcontractor
   * @param externalProductionId The external production id
   * @returns The promise
   */
  public resendSubcontractorLink(externalProductionId: string): Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ externalProductionId: string }, void>(
      this.functions, 'resend_subcontractor_link_gen2')(
      { externalProductionId });
  }

  /**
   * Resend photo notification email to all notifee emails
   * @param externalProductionId The external production id
   * @returns The promise
   */
  public resendPhotoNotification(externalProductionId: string): Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ externalProductionId: string }, void>(
      this.functions, 'resend_photo_notification_gen2')(
      { externalProductionId });
  }

  /**
   * Send cancellation email
   * @param externalProductionId The external production id
   * @param requestIds The requests ids to cancel
   * @returns The promise
   */
  public sendCancellation(externalProductionId: string, requestIds: string[])
    : Promise<HttpsCallableResult<void>> {
    return httpsCallable<{ externalProductionId: string, requestIds: string[] }, void>(
      this.functions, 'send_cancellation_gen2')(
      { externalProductionId, requestIds });
  }

  /**
   * Finalizes an external production by its identifier and retrieves its state and associated time slots.
   * @param externalProductionId - The unique identifier for the external production to finalize.
   * @returns A promise that resolves to an object containing the state of the external production and an array of
   * time slots.
   */
  public async finalize(externalProductionId: string):
    Promise<{ state: ExternalProductionState, timeSlots: TimeSlot[] }> {
    const { data } = await httpsCallable<{ externalProductionId: string },
      { state: ExternalProductionState, timeSlots: { start:number, end: number }[] }>(
        this.functions, 'finalize_operation_gen2')({ externalProductionId });
    return {
      state: data.state,
      timeSlots: data.timeSlots.map(slot => ({
        start: new Date(slot.start),
        end: new Date(slot.end)
      }))
    };
  }

  /**
   * Create an external production
   * @param externalProduction The external production
   * @returns Id of the created external production
   */
  async create(externalProduction: ExternalProduction): Promise<string> {
    const ref = collection(this.firestore, 'external-productions').withConverter(ExternalProduction.converter);
    const created = await addDoc(ref, ObjectHelper.anonymize(externalProduction));
    return created.id;
  }

  /**
   * Retrieves a list of customer installations for a specific organization and order.
   * Fetches data from an external source using the provided organization and order identifiers.
   * @param organizationId - The unique identifier of the organization.
   * @param orderId - The unique identifier of the order.
   * @returns A promise that resolves to an array of installations related to the specified order.
   */
  async getCustomerInstallations(organizationId: string, orderId: string): Promise<ExternalProduction[]> {
    const externalProductions = await httpsCallable<{ organizationId: string, orderId: string },
      { externalProductions: ExternalProduction[]}>(
        this.functions, 'customer_installations_gen2')({ organizationId, orderId });
    return externalProductions.data.externalProductions;
  }


  /**
   * Get installation photos by photo view token (no auth required)
   * @param id The external production id
   * @param token The photo view token
   * @returns A promise with erpId and installation locations
   */
  public getInstallationPhotosByToken(id: string, token: string)
    : Promise<{ erpId: string, installationLocations: any[] } | null> {
    this.dataService.dataLoading = true;
    return httpsCallable<
      { id: string, token: string },
      { erpId: string, installationLocations: any[] }
    >(this.functions, 'get_installation_photos_by_token_gen2')({ id, token })
      .then(result => result.data)
      .catch(() => null)
      .finally(() => this.dataService.dataLoading = false);
  }

  /**
   * Get order by id with token
   * @param id The order id
   * @param token The authorisation token
   * @param subcontractorEmail The subcontractor email
   * @returns A promise with the external production and subcontract request
   */
  public getExternalProductionWithToken(id: string, token: string, subcontractorEmail: string)
    : Promise<{ externalProduction: ExternalProduction, subcontractRequest: SubcontractRequest | null } | null> {
    return httpsCallable<
      { id: string, token: string, subcontractorEmail: string },
      { externalProduction: ExternalProduction, subcontractRequest: SubcontractRequest | null }
    >(this.functions, 'get_external_production_by_token_gen2')({ id, token, subcontractorEmail })
      .then(result => ({
        externalProduction: new ExternalProduction(result.data.externalProduction),
        subcontractRequest: result.data.subcontractRequest
          ? new SubcontractRequest(result.data.subcontractRequest)
          : null,
      }))
      .catch((error: any) => {
        // Re-throw unexpected errors so the caller can distinguish them from auth failures
        if (error?.code?.startsWith('functions/')) {
          return null;
        }
        throw error;
      });
  }
}
